"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Heart } from "lucide-react";
import Link from "next/link";

// Stacked loyalty-card visuals — 3 visible, "Pepitos" on top. Visual only: Redy loyalty is
// tracked by phone number in Redy, NOT a Wallet pass (no Apple/Google Wallet loyalty card).
const CARDS = [
  { brand: "PEPITOS", tagline: "Birria · Dallas", color: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)", visits: 7, goal: 10 },
  { brand: "URBAN COFFEE", tagline: "Espresso · Austin", color: "linear-gradient(135deg, #1A1A1A 0%, #3F3F3F 100%)", visits: 4, goal: 8 },
  { brand: "TACO STAND", tagline: "Street tacos · Houston", color: "linear-gradient(135deg, #4A7AE8 0%, #1A1A1A 100%)", visits: 2, goal: 10 },
];

export default function LoyaltyHero() {
  const { t } = useLang();
  const [customers, setCustomers] = useState(0);
  const target = 1247;

  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const dur = 3500;
    const tick = (now: number) => {
      const tt = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - tt, 3);
      setCustomers(Math.round(target * eased));
      if (tt < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-loyalty-crm.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAF8]/80 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Loyalty & CRM · The list that's yours", "Lealtad & CRM · La lista que es tuya")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("Your customers.", "Tus clientes.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Your list. Yours.", "Tu lista. Tuya.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Points and rewards tracked by phone number — no card to carry, no app to download. A customer database that grows with every order: who comes back, how often, what they spend. Captured at your counter and on your page, not rented from a marketplace.",
            "Puntos y premios por número de teléfono — sin tarjeta que cargar, sin app que descargar. Una base de clientes que crece con cada orden: quién vuelve, cada cuánto y cuánto gasta. Capturada en tu mostrador y en tu página, no alquilada a un marketplace."
          )}
        </motion.p>

        {/* Hero showcase: stacked wallet cards + counter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center max-w-[1100px] mx-auto mb-12"
        >
          {/* Wallet stack */}
          <div className="lg:col-span-7 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[340px] h-[420px]">
              {CARDS.map((card, i) => {
                const isTop = i === 0;
                return (
                  <motion.div
                    key={card.brand}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.7,
                      delay: 0.7 + 0.15 * (CARDS.length - i),
                      ease: [0.21, 0.47, 0.32, 0.98],
                    }}
                    className="absolute left-0 right-0 rounded-[20px] p-5 text-white overflow-hidden"
                    style={{
                      top: i * 18,
                      background: card.color,
                      boxShadow: `0 ${20 + i * 6}px ${50 + i * 10}px rgba(0,0,0,${0.18 + i * 0.04})`,
                      zIndex: 10 - i,
                      transform: `scale(${1 - i * 0.04})`,
                      transformOrigin: "top center",
                    }}
                  >
                    {/* Atmosphere */}
                    <div
                      className="absolute inset-0 opacity-25 pointer-events-none"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%)",
                      }}
                    />

                    <div className="relative">
                      {/* Top row */}
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-white/70">
                            {t("Loyalty points", "Puntos de lealtad")}
                          </div>
                          <div className="text-[18px] font-black tracking-tight">{card.brand}</div>
                        </div>
                        <Heart
                          className="w-4 h-4 text-white/70"
                          fill={isTop ? "currentColor" : "none"}
                        />
                      </div>

                      <div className="text-[10px] font-bold text-white/75 mb-5">{card.tagline}</div>

                      {/* Punches */}
                      {isTop && (
                        <>
                          <div className="text-[9px] font-bold uppercase tracking-wider text-white/60 mb-2">
                            {t("Points on every order", "Puntos en cada orden")}
                          </div>
                          <div className="flex gap-1.5 mb-3">
                            {Array.from({ length: card.goal }).map((_, j) => {
                              const filled = j < card.visits;
                              return (
                                <motion.div
                                  key={j}
                                  initial={{ scale: 0.5, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  transition={{ duration: 0.3, delay: 1.4 + 0.06 * j }}
                                  className="flex-1 h-6 rounded-md flex items-center justify-center"
                                  style={{
                                    backgroundColor: filled
                                      ? "rgba(255,255,255,0.95)"
                                      : "rgba(255,255,255,0.15)",
                                  }}
                                >
                                  {filled && (
                                    <Heart className="w-3 h-3" style={{ color: "#D7263D" }} fill="currentColor" />
                                  )}
                                </motion.div>
                              );
                            })}
                          </div>
                          <div className="text-[11px] font-black text-white">
                            {t(`${(card.goal - card.visits) * 10} more points → free taco`, `${(card.goal - card.visits) * 10} puntos más → taco gratis`)}
                          </div>
                        </>
                      )}

                      {!isTop && (
                        <div className="text-[11px] font-bold text-white/70">
                          {card.visits * 10}/{card.goal * 10} {t("pts", "pts")}
                        </div>
                      )}
                    </div>

                    {/* Phone-number badge — top card only (not a Wallet pass) */}
                    {isTop && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2.0, duration: 0.4 }}
                        className="absolute bottom-4 right-5 bg-black text-white text-[10px] font-bold px-3 py-1.5 rounded-md"
                      >
                        {t("Tracked by phone", "Por teléfono")}
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Customer counter */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="bg-white border border-black/5 rounded-[24px] p-7 w-full max-w-[340px] shadow-[0_30px_80px_rgba(0,0,0,0.06)]"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                  {t("Your customers", "Tus clientes")}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#D7263D" }}>
                    {t("Growing", "Creciendo")}
                  </span>
                </div>
              </div>

              <div
                className="text-[80px] font-black tabular-nums leading-none mb-2"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {customers.toLocaleString()}
              </div>
              <div className="text-[12px] font-bold text-redy-secondary">
                {t("Across the year. Yours.", "A lo largo del año. Tuyos.")}
              </div>

              {/* Mini split: top customers */}
              <div className="mt-6 pt-5 border-t border-black/5 space-y-2.5">
                {[
                  { label: t("Returning visitors", "Visitantes recurrentes"), value: "68%" },
                  { label: t("Avg visits / customer", "Visitas prom / cliente"), value: "3.2" },
                  { label: t("Top 100 spend", "Top 100 gastan"), value: "$28k" },
                ].map((s) => (
                  <div key={s.label} className="flex justify-between items-baseline text-[12px]">
                    <span className="text-redy-secondary font-medium">{s.label}</span>
                    <span className="text-redy-charcoal font-black tabular-nums">{s.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Start owning your list →", "Empieza a poseer tu lista →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
